import React, { useEffect, useState, useCallback } from 'react';
import './App.css';

/* ------------------------------------------------------------------ */
/* Theme toggle — persists to localStorage, mirrors the studio site    */
/* ------------------------------------------------------------------ */

const STORAGE_KEY = 'theme';
const LIGHT_CLASS = 'theme-light';

const SunIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

function ThemeToggle() {
  const [isLight, setIsLight] = useState(false);

  useEffect(() => {
    setIsLight(document.documentElement.classList.contains(LIGHT_CLASS));
  }, []);

  const toggle = () => {
    const next = !isLight;
    setIsLight(next);
    const root = document.documentElement;
    root.classList.toggle(LIGHT_CLASS, next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'light' : 'dark');
    } catch (e) {
      /* localStorage unavailable — ignore */
    }
  };

  const label = isLight ? 'Switch to dark mode' : 'Switch to light mode';

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={label}
      title={label}
    >
      <span
        className={`theme-toggle-side ${isLight ? 'is-active' : ''}`}
        aria-hidden="true"
      >
        <SunIcon />
      </span>
      <span
        className={`theme-toggle-side ${!isLight ? 'is-active' : ''}`}
        aria-hidden="true"
      >
        <MoonIcon />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

const UPWORK_URL = 'https://www.upwork.com/agencies/1615126314945720320/';
const CONTACT_EMAIL = 'hello@elancore.com';

const NAV_LINKS = [
  { href: '#services', label: 'Services' },
  { href: '#approach', label: 'Approach' },
  { href: '#studio', label: 'Studio' },
  { href: '#contact', label: 'Contact' }
];

const SERVICES = [
  {
    num: '01',
    icon: '💻',
    title: 'Full-Stack Web Development',
    body:
      'Production web platforms built on React, Next.js, and modern Python/Node back ends — fast, accessible, and engineered to scale with your business.',
    tags: ['React', 'Next.js', 'FastAPI', 'Node']
  },
  {
    num: '02',
    icon: '🤖',
    title: 'ML / AI Integration',
    body:
      'We weave intelligent features into your product — recommendations, search, vision, and forecasting — and wire them into reliable, observable pipelines.',
    tags: ['PyTorch', 'RAG', 'Vision', 'MLOps']
  },
  {
    num: '03',
    icon: '🧠',
    title: 'Custom LLM Solutions',
    body:
      'Fine-tuned models, retrieval-augmented assistants, and agentic workflows tailored to your domain, data, and guardrails — not a generic chatbot.',
    tags: ['Fine-tuning', 'Agents', 'Evals', 'LLMOps']
  },
  {
    num: '04',
    icon: '📱',
    title: 'Web & Mobile Design',
    body:
      'Interface design and design systems that turn complex products into intuitive experiences — from first wireframe to polished, shippable UI.',
    tags: ['UX', 'UI', 'Design Systems', 'Prototyping']
  },
  {
    num: '05',
    icon: '📈',
    title: 'Digital Marketing',
    body:
      'Strategy, SEO, and performance campaigns that put your product in front of the right people and turn attention into measurable growth.',
    tags: ['SEO', 'Analytics', 'Campaigns', 'Content']
  },
  {
    num: '06',
    icon: '🎨',
    title: 'Branding & Identity',
    body:
      'Logos, brand systems, and visual language that give your company a distinct, confident presence across every touchpoint.',
    tags: ['Logo', 'Brand', 'Guidelines', 'Assets']
  }
];

const APPROACH = [
  {
    num: '01',
    title: 'Discover',
    body:
      'We start with your goals, users, and constraints — mapping the problem before a single line of code is written.'
  },
  {
    num: '02',
    title: 'Design',
    body:
      'Architecture, interface, and data model take shape together, validated with prototypes you can actually click through.'
  },
  {
    num: '03',
    title: 'Build',
    body:
      'Tight, iterative sprints with working software at the end of each one. You see progress weekly, not at the finish line.'
  },
  {
    num: '04',
    title: 'Ship & scale',
    body:
      'Automated deploys, monitoring, and a handover that leaves your team ready to run and grow what we built together.'
  }
];

const TESTIMONIALS = [
  {
    quote:
      'Elancore took a vague idea and turned it into a polished product faster than we thought possible. They communicate like an in-house team.',
    name: 'Product Lead',
    role: 'B2B SaaS startup'
  },
  {
    quote:
      'The LLM assistant they built actually understands our domain. Adoption across the team was immediate.',
    name: 'Head of Operations',
    role: 'Logistics company'
  }
];

/* ------------------------------------------------------------------ */
/* Sections                                                            */
/* ------------------------------------------------------------------ */

function Header() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <header className={`site-head ${open ? 'is-open' : ''}`}>
      <div className="site-head-inner">
        <a className="brand" href="#top" onClick={close}>
          Elancore<span className="brand-dot">.</span>
        </a>

        <button
          type="button"
          className="nav-burger"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen(v => !v)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className="site-nav" aria-label="Primary">
          <ul>
            {NAV_LINKS.map(link => (
              <li key={link.href}>
                <a href={link.href} onClick={close}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="site-nav-actions">
            <ThemeToggle />
            <a className="nav-cta" href="#contact" onClick={close}>
              Start a project
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <header className="hero" id="top">
      <span className="kicker">Software &amp; AI Studio</span>
      <h1 className="hero-title">
        We build the web,
        <br />
        and the <em>intelligence</em> inside it.
      </h1>
      <p className="hero-sub">
        Elancore is a product studio crafting full-stack web platforms, ML/AI
        integrations, and custom language models for teams that want to ship
        something real.
      </p>
      <div className="hero-ctas">
        <a className="btn btn-primary" href="#contact">
          Start a project <span aria-hidden="true">→</span>
        </a>
        <a className="btn btn-ghost" href="#services">
          Explore services
        </a>
      </div>
    </header>
  );
}

function Services() {
  return (
    <section className="section" id="services">
      <div className="section-head">
        <span className="section-num">01</span>
        <h2 className="section-title">
          What we <em>do</em>
        </h2>
      </div>
      <p className="section-sub">
        Six disciplines, one team. We take products from first sketch to
        production — and keep them sharp afterward.
      </p>
      <div className="service-grid">
        {SERVICES.map(s => (
          <article className="service-card" key={s.num}>
            <div className="service-card-top">
              <span className="service-icon" role="img" aria-label={s.title}>
                {s.icon}
              </span>
              <span className="service-num">{s.num}</span>
            </div>
            <h3 className="service-title">{s.title}</h3>
            <p className="service-body">{s.body}</p>
            <ul className="tag-row" aria-label="Technologies">
              {s.tags.map(t => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section className="section" id="approach">
      <div className="section-head">
        <span className="section-num">02</span>
        <h2 className="section-title">
          How we <em>work</em>
        </h2>
      </div>
      <p className="section-sub">
        A focused process that keeps you in the loop and gets working software in
        your hands early.
      </p>
      <div className="approach-grid">
        {APPROACH.map(step => (
          <div className="approach-step" key={step.num}>
            <span className="approach-num">{step.num}</span>
            <h3 className="approach-title">{step.title}</h3>
            <p className="approach-body">{step.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Studio() {
  return (
    <section className="section" id="studio">
      <div className="section-head">
        <span className="section-num">03</span>
        <h2 className="section-title">
          The <em>studio</em>
        </h2>
      </div>
      <div className="studio-grid">
        <div className="studio-lead">
          <p>
            Elancore is a small, senior team that pairs strong engineering with
            real AI depth. We&rsquo;ve shipped full-stack platforms, integrated
            machine learning into live products, and built custom LLM tooling
            from the ground up.
          </p>
          <p>
            We work like an extension of your team — direct, transparent, and
            obsessive about the details that make software feel effortless.
          </p>
          <div className="studio-quotes">
            {TESTIMONIALS.map(t => (
              <blockquote className="quote" key={t.name}>
                <p>&ldquo;{t.quote}&rdquo;</p>
                <cite>
                  <strong>{t.name}</strong> · {t.role}
                </cite>
              </blockquote>
            ))}
          </div>
        </div>
        <aside className="studio-aside">
          <h3>Why teams choose us</h3>
          <ul className="check-list">
            <li>Senior engineers on every project — no hand-offs to juniors</li>
            <li>AI expertise that goes beyond wrapping an API</li>
            <li>Weekly working software, not status decks</li>
            <li>Clean handover and documentation you&rsquo;ll actually use</li>
          </ul>
          <a
            className="btn btn-ghost"
            href={UPWORK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            View us on Upwork <span aria-hidden="true">↗</span>
          </a>
        </aside>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="section section-contact" id="contact">
      <div className="contact-card">
        <span className="kicker">Let&rsquo;s build</span>
        <h2 className="contact-title">
          Have a project in mind?
        </h2>
        <p className="contact-sub">
          Tell us what you&rsquo;re building. We&rsquo;ll get back to you with
          how we can help — usually within a day.
        </p>
        <div className="contact-actions">
          <a className="btn btn-primary" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          <a
            className="btn btn-ghost"
            href={UPWORK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Hire on Upwork <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-foot">
      <div className="site-foot-inner">
        <span className="site-foot-copy">
          &copy; {new Date().getFullYear()} Elancore Agency · Software &amp; AI
          Studio
        </span>
        <div className="site-foot-right">
          <nav className="site-foot-links" aria-label="Footer">
            <a href={`mailto:${CONTACT_EMAIL}`}>Email</a>
            <a href={UPWORK_URL} target="_blank" rel="noopener noreferrer">
              Upwork
            </a>
            <a href="#top">Back to top</a>
          </nav>
          <ThemeToggle />
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="site-wrapper">
      <Header />
      <main className="site-main">
        <Hero />
        <Services />
        <Approach />
        <Studio />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
